import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Check, ArrowRight, VideoCamera, CaretDown } from "@phosphor-icons/react";
import axios from "axios";
import DemoBookingModal from "@/components/DemoBookingModal";

const BASE_URL = "https://app.trappkonsulten.se";
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

/**
 * Branschspecifik landningssida — content hämtas från MongoDB via
 * `/api/landing-pages/{slug}`. Super admin kan skapa/redigera sidor från
 * Leads-panelen (fliken "Landningssidor") utan kodrelease.
 */
export default function IndustryLanding({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const [industry, setIndustry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API_BASE}/api/landing-pages/${slug}`)
      .then((r) => setIndustry(r.data))
      .catch((err) => {
        if (err.response?.status === 404) setNotFound(true);
        else setNotFound(true); // säkerhetsvarv — visa 404 vid alla fel
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // Intern trafik-tracking (fire-and-forget, ignorerar fel).
  // Botar filtreras server-side. Ingen cookie sätts.
  useEffect(() => {
    if (!slug) return;
    axios
      .post(`${API_BASE}/api/landing-stats/track`, {
        slug,
        referrer: document.referrer || "",
      })
      .catch(() => {});
  }, [slug]);

  // CTA-tracking måste överleva sidnavigering (t.ex. <Link to="/register"> som
  // avbryter pågående XHR). Använder navigator.sendBeacon som garanterat
  // skickas även när sidan lämnas. Fallback: axios.
  const trackCta = (which) => {
    const url = `${API_BASE}/api/landing-stats/track-cta`;
    const payload = JSON.stringify({ slug, cta: which });
    try {
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        const blob = new Blob([payload], { type: "application/json" });
        const sent = navigator.sendBeacon(url, blob);
        if (sent) return;
      }
    } catch (_e) { /* fall through till axios */ }
    axios.post(url, { slug, cta: which }).catch(() => {});
  };

  useEffect(() => {
    if (!industry) return;
    const canonicalUrl = `${BASE_URL}/tidrapportering-${industry.slug}`;
    document.title = industry.meta_title;
    setMeta("description", industry.meta_description);
    setMeta("og:title", industry.meta_title, "property");
    setMeta("og:description", industry.meta_description, "property");
    setMeta("og:url", canonicalUrl, "property");
    setMeta("og:type", "article", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", industry.meta_title);
    setMeta("twitter:description", industry.meta_description);
    setCanonical(canonicalUrl);

    setJsonLd("industry-article", {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: industry.schema_org_article.headline,
      description: industry.schema_org_article.description,
      inLanguage: "sv-SE",
      author: { "@type": "Organization", name: "Tidsrapporten" },
      publisher: {
        "@type": "Organization",
        name: "Tidsrapporten",
        logo: { "@type": "ImageObject", url: `${BASE_URL}/logo512.png` },
      },
      mainEntityOfPage: canonicalUrl,
    });

    setJsonLd("industry-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: industry.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });

    setJsonLd("industry-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Tidsrapporten",
          item: `${BASE_URL}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Branschsidor",
          item: `${BASE_URL}/#branscher`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: industry.title,
          item: canonicalUrl,
        },
      ],
    });

    return () => {
      removeJsonLd("industry-article");
      removeJsonLd("industry-faq");
      removeJsonLd("industry-breadcrumb");
    };
  }, [industry]);

  if (notFound) return <Navigate to="/" replace />;
  if (loading || !industry) {
    return (
      <div
        className="min-h-screen bg-app text-text flex items-center justify-center"
        data-testid="industry-landing-loading"
      >
        <div className="text-sm text-text-muted">Laddar…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app text-text" data-testid={`industry-landing-${industry.slug}`}>
      {/* HEADER */}
      <header className="border-b border-app bg-surface">
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="industry-back-to-home">
            <span className="h-8 w-8 rounded bg-brand flex items-center justify-center font-heading text-sm font-bold text-white">
              T
            </span>
            <span className="font-heading text-lg">Tidsrapporten</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            <Link to="/pricing" className="hover:text-brand transition-colors hidden sm:inline">
              Priser
            </Link>
            <Link to="/login" className="hover:text-brand transition-colors hidden sm:inline">
              Logga in
            </Link>
            <Link
              to="/register"
              onClick={() => trackCta("primary")}
              className="bg-brand text-white px-4 py-2 rounded-md font-medium hover:bg-brand-hover transition-colors"
              data-testid="industry-header-cta"
            >
              Starta gratis
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-surface to-app">
        <div className="mx-auto max-w-6xl px-6 py-16 lg:py-24 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <div className="inline-block text-xs tracking-widest uppercase text-brand font-medium mb-4">
              För {industry.title.toLowerCase()}
            </div>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-tight"
              data-testid="industry-hero-h1"
            >
              {industry.h1}
            </h1>
            <p className="mt-6 text-lg text-text-muted leading-relaxed max-w-2xl">
              {industry.hero_lead}
            </p>
            <ul className="mt-6 space-y-2.5">
              {industry.hero_bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <span className="mt-0.5 text-brand">
                    <Check size={18} weight="bold" />
                  </span>
                  <span className="text-text">{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/register"
                onClick={() => trackCta("primary")}
                className="bg-brand text-white px-6 py-3 rounded-md font-medium hover:bg-brand-hover transition-colors flex items-center gap-2"
                data-testid="industry-hero-primary-cta"
              >
                {industry.cta.primary}
                <ArrowRight size={18} weight="bold" />
              </Link>
              <button
                onClick={() => { trackCta("secondary"); setDemoOpen(true); }}
                className="border border-app px-6 py-3 rounded-md font-medium hover:border-brand transition-colors flex items-center gap-2"
                data-testid="industry-hero-secondary-cta"
              >
                <VideoCamera size={18} weight="fill" />
                {industry.cta.secondary}
              </button>
            </div>
          </div>

          <div className="lg:col-span-2 hidden lg:block">
            <HeroPhoneMockup industry={industry} />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-surface border-y border-app">
        <div className="mx-auto max-w-6xl px-6 py-16 lg:py-20">
          <h2 className="font-heading text-3xl sm:text-4xl mb-3">
            Så här löser Tidsrapporten er vardag
          </h2>
          <p className="text-text-muted leading-relaxed max-w-2xl mb-10">
            Ingen ny inloggning per anställd. Ingen komplicerad projektstruktur.
            Bara det ni behöver, i den ordning ni behöver det.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industry.features.map((f, i) => (
              <div
                key={i}
                className="bg-app border border-app rounded-xl p-6 hover:border-brand transition-colors"
                data-testid={`industry-feature-${i}`}
              >
                <h3 className="font-heading text-lg leading-tight mb-3">{f.title}</h3>
                <p className="text-sm text-text-muted leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="bg-app">
        <div className="mx-auto max-w-6xl px-6 py-16 lg:py-20">
          <h2 className="font-heading text-3xl sm:text-4xl mb-3">En app — flera perspektiv</h2>
          <p className="text-text-muted leading-relaxed max-w-2xl mb-10">
            Samma verktyg, olika behov. Här är hur olika roller använder Tidsrapporten
            dagligen.
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            {industry.use_cases.map((uc, i) => (
              <div
                key={i}
                className="bg-surface border border-app rounded-xl p-6"
                data-testid={`industry-usecase-${i}`}
              >
                <div className="text-xs tracking-widest uppercase text-brand font-medium mb-2">
                  {uc.role}
                </div>
                <p className="text-sm leading-relaxed">{uc.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-surface border-y border-app">
        <div className="mx-auto max-w-3xl px-6 py-16 lg:py-20">
          <h2 className="font-heading text-3xl sm:text-4xl mb-3">Vanliga frågor</h2>
          <p className="text-text-muted mb-8">
            Hittar du inte svaret?{" "}
            <button
              onClick={() => { trackCta("secondary"); setDemoOpen(true); }}
              className="underline text-brand hover:no-underline"
            >
              Boka en 20-min demo
            </button>{" "}
            och ställ frågor direkt.
          </p>
          <div className="space-y-3">
            {industry.faq.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div
                  key={i}
                  className="border border-app rounded-lg bg-app overflow-hidden"
                  data-testid={`industry-faq-${i}`}
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-surface transition-colors"
                  >
                    <span className="font-medium text-sm sm:text-base">{item.q}</span>
                    <CaretDown
                      size={18}
                      weight="bold"
                      className={`text-text-muted transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-4 text-sm text-text-muted leading-relaxed border-t border-app pt-4">
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand text-white">
        <div className="mx-auto max-w-4xl px-6 py-16 lg:py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl leading-tight">
            Redo att sluta jaga tidsedlar?
          </h2>
          <p className="mt-4 text-white/90 text-lg leading-relaxed max-w-2xl mx-auto">
            30 dagar gratis. Ingen bindning. Ingen kortuppgift vid registrering.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/register"
              onClick={() => trackCta("primary")}
              className="bg-white text-brand px-6 py-3 rounded-md font-medium hover:bg-white/90 transition-colors flex items-center gap-2"
              data-testid="industry-final-cta-register"
            >
              {industry.cta.primary}
              <ArrowRight size={18} weight="bold" />
            </Link>
            <button
              onClick={() => { trackCta("secondary"); setDemoOpen(true); }}
              className="border border-white/40 text-white px-6 py-3 rounded-md font-medium hover:bg-white/10 transition-colors flex items-center gap-2"
              data-testid="industry-final-cta-demo"
            >
              <VideoCamera size={18} weight="fill" />
              {industry.cta.secondary}
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-sidebar text-sidebar-text/70">
        <div className="mx-auto max-w-6xl px-6 py-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <Link to="/" className="flex items-center gap-2 text-sidebar-text">
              <span className="h-7 w-7 rounded bg-brand flex items-center justify-center font-heading text-xs font-bold text-white">
                T
              </span>
              <span className="font-heading text-base">Tidsrapporten</span>
            </Link>
            <p className="mt-3 text-xs max-w-md">
              Tidrapportering och personalliggare för hantverkare, byggföretag och konsulter.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <Link to="/" className="hover:text-white transition-colors">Hem</Link>
            <Link to="/pricing" className="hover:text-white transition-colors">Priser</Link>
            <Link to="/login" className="hover:text-white transition-colors">Logga in</Link>
            <Link to="/register" onClick={() => trackCta("primary")} className="hover:text-white transition-colors">Starta gratis</Link>
          </div>
        </div>
        <div className="border-t border-sidebar-text/10">
          <div className="mx-auto max-w-6xl px-6 py-5 text-xs">
            © {new Date().getFullYear()} Tidsrapporten. Alla rättigheter förbehållna.
          </div>
        </div>
      </footer>

      <DemoBookingModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}

// --- SEO helpers ---
function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(url) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

function setJsonLd(id, data) {
  removeJsonLd(id);
  const el = document.createElement("script");
  el.setAttribute("type", "application/ld+json");
  el.setAttribute("data-jsonld-id", id);
  el.textContent = JSON.stringify(data);
  document.head.appendChild(el);
}

function removeJsonLd(id) {
  const el = document.querySelector(`script[data-jsonld-id="${id}"]`);
  if (el) el.remove();
}

/**
 * Hero-mockup: en tiltad "telefon" som visar stämpelklockan mid-shift för
 * ett branschspecifikt objekt. Använder industry.slug för att välja
 * kontextuell objekt-titel. Ren CSS — inga bilder, skalar rent.
 */
function HeroPhoneMockup({ industry }) {
  // Branschspecifik "objektstitel" i mocken — känns produktnära
  const objectByIndustry = {
    bygg: { name: "Kv. Ekbacken 12", tag: "Personalliggare aktiv" },
    snickeri: { name: "Villa Lindström", tag: "Trappmontage" },
    vvs: { name: "Storgatan 7, Lgh 1201", tag: "Badrumsrenovering" },
    maleri: { name: "Villa Almgren", tag: "Vardagsrum + hall" },
    fastighetsservice: { name: "BRF Solängen", tag: "Rondering" },
    "mark-anlaggning": { name: "Fjärrvärme E45", tag: "Personalliggare aktiv" },
    "golv-kakel": { name: "Badrum – Kv. Björken", tag: "Kaklat golv" },
    el: { name: "Nybyggnation Fsk. Bäcken", tag: "Elinstallation" },
    "fasad-tak": { name: "Fasadrenovering Ö. gatan", tag: "Personalliggare aktiv" },
    "stad-hemservice": { name: "Familjen Persson", tag: "Hemstädning • RUT" },
  };
  const obj = objectByIndustry[industry?.slug] || { name: "Villa Lindström", tag: "Objekt" };

  return (
    <div className="relative aspect-square flex items-center justify-center">
      {/* Bakgrund — subtil brand-glow, ingen tom grid */}
      <div
        className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-brand/15 via-brand/5 to-transparent blur-xl"
        aria-hidden="true"
      />

      {/* Telefonram — svart bezel, lätt tiltad för dynamik */}
      <div
        className="relative w-[260px] h-[520px] rounded-[2.5rem] bg-neutral-900 shadow-2xl p-3 rotate-[-4deg] hover:rotate-0 transition-transform duration-500"
        data-testid="hero-phone-mockup"
      >
        {/* Notch */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 bg-neutral-900 rounded-b-2xl z-10 border-b border-neutral-800" />

        {/* Skärm */}
        <div className="w-full h-full rounded-[2rem] bg-white overflow-hidden flex flex-col relative">
          {/* Status bar */}
          <div className="flex justify-between items-center px-5 pt-3 text-[11px] font-medium text-neutral-800">
            <span>08:14</span>
            <span className="tracking-wide">••• 5G</span>
          </div>

          {/* Header */}
          <div className="px-5 pt-4 pb-3 border-b border-neutral-100">
            <div className="text-[10px] tracking-widest uppercase text-brand font-semibold">
              Stämplad in
            </div>
            <div className="mt-1 font-heading text-[15px] leading-tight text-neutral-900">
              {obj.name}
            </div>
            <div className="text-[11px] text-neutral-500 mt-0.5">{obj.tag}</div>
          </div>

          {/* Timer-block */}
          <div className="flex-1 flex flex-col items-center justify-center px-4">
            {/* Pulsande grön prick */}
            <div className="flex items-center gap-2 mb-3">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <span className="text-[10px] font-semibold tracking-widest uppercase text-emerald-600">
                Pågår
              </span>
            </div>

            <div
              className="font-heading text-[42px] text-neutral-900 leading-none tabular-nums"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              02:14:33
            </div>
            <div className="text-[11px] text-neutral-500 mt-2">
              Startade 06:00
            </div>

            {/* Mini stats */}
            <div className="mt-6 grid grid-cols-2 gap-3 w-full text-center">
              <div className="rounded-lg bg-neutral-50 border border-neutral-100 py-2">
                <div className="text-[9px] text-neutral-500 uppercase tracking-wide">
                  Idag
                </div>
                <div className="text-[13px] font-semibold text-neutral-900">
                  8h 12min
                </div>
              </div>
              <div className="rounded-lg bg-neutral-50 border border-neutral-100 py-2">
                <div className="text-[9px] text-neutral-500 uppercase tracking-wide">
                  Vecka
                </div>
                <div className="text-[13px] font-semibold text-neutral-900">
                  32h 40min
                </div>
              </div>
            </div>
          </div>

          {/* Stämpla ut-knapp */}
          <div className="px-4 pb-5">
            <div className="w-full bg-brand text-white text-center py-3 rounded-xl text-[13px] font-semibold shadow-sm">
              Stämpla ut
            </div>
            <div className="text-center text-[10px] text-neutral-400 mt-2">
              Fungerar offline · synkar automatiskt
            </div>
          </div>
        </div>
      </div>

      {/* Flytande "notif"-badge — social proof */}
      <div className="absolute top-8 -left-2 bg-white rounded-xl shadow-lg border border-neutral-100 px-3 py-2 rotate-[-6deg] hidden xl:block">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center">
            <Check size={16} weight="bold" className="text-brand" />
          </div>
          <div>
            <div className="text-[11px] font-semibold text-neutral-900">
              Fakturaunderlag klart
            </div>
            <div className="text-[10px] text-neutral-500">PDF · 30 sek sedan</div>
          </div>
        </div>
      </div>

      {/* Flytande pris-tag — nu subtil, längst ner */}
      <div className="absolute bottom-4 -right-2 bg-white rounded-xl shadow-lg border border-neutral-100 px-4 py-2.5 rotate-[3deg] hidden xl:block">
        <div className="text-[10px] text-neutral-500 uppercase tracking-widest">
          Från
        </div>
        <div className="text-lg font-heading text-brand leading-none">
          149 kr<span className="text-[11px] text-neutral-500 font-sans"> /anv/mån</span>
        </div>
        <div className="text-[10px] text-neutral-500 mt-0.5">30 dagar gratis</div>
      </div>
    </div>
  );
}

